import React, { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { lmsConnectionsApi } from "../../lib/api"

const Header = () => {
  const { user, signOut, isParent, isStudent } = useAuth()
  const location = useLocation()
  const [connections, setConnections] = useState([])
  const [syncing, setSyncing] = useState(false)
  const [lastSyncAt, setLastSyncAt] = useState(null)

  useEffect(() => {
    if (isStudent) {
      fetchConnections()
    }
  }, [isStudent])

  const fetchConnections = async () => {
    try {
      const data = await lmsConnectionsApi.list()
      setConnections(data.lms_connections || [])
      // Find the most recent sync time across all connections
      const lastSync = data.lms_connections?.reduce((latest, conn) => {
        if (!conn.last_sync_at) return latest
        if (!latest) return conn.last_sync_at
        return new Date(conn.last_sync_at) > new Date(latest) ? conn.last_sync_at : latest
      }, null)
      setLastSyncAt(lastSync)
      // Check if any connection is syncing
      setSyncing(data.lms_connections?.some(c => c.syncing) || false)
    } catch (error) {
      console.error("Failed to fetch connections:", error)
    }
  }

  const handleSync = async () => {
    if (syncing || connections.length === 0) return
    try {
      setSyncing(true)
      // Sync all enabled connections
      for (const conn of connections.filter(c => c.enabled)) {
        await lmsConnectionsApi.sync(conn.id)
      }
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const data = await lmsConnectionsApi.list()
        const anySyncing = data.lms_connections?.some(c => c.syncing)
        if (!anySyncing) {
          setSyncing(false)
          setConnections(data.lms_connections || [])
          clearInterval(pollInterval)
          window.location.reload()
        }
      }, 2000)
    } catch (error) {
      setSyncing(false)
      console.error("Sync failed:", error)
    }
  }

  const formatLastSync = (dateStr) => {
    if (!dateStr) return "Never"
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays}d ago`
  }

  const hasConnections = connections.length > 0

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-[var(--cream)] sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="/images/heartilylogo.png"
              alt="Heartily"
              className="h-8 drop-shadow-sm group-hover:drop-shadow-md transition-all"
            />
          </Link>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Link to="/ai-assistants" aria-current={location.pathname === "/ai-assistants" ? "page" : undefined}
              className="inline-flex min-h-11 items-center rounded-xl px-2 sm:px-3 text-sm font-medium text-[var(--ink-muted)] hover:bg-[var(--cream)] hover:text-[var(--ink)]">
              AI guide
            </Link>
            {/* Parent Dashboard Link */}
            {isParent && (
              <Link
                to="/parent-dashboard"
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-xl transition-all ${
                  location.pathname === "/parent-dashboard"
                    ? "bg-[var(--gold)]/10 text-[var(--gold)]"
                    : "text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--cream)]"
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="hidden sm:inline font-medium">Dashboard</span>
              </Link>
            )}

            {/* Sync Button */}
            {isStudent && hasConnections && (
              <button
                onClick={handleSync}
                disabled={syncing}
                className={`
                  flex items-center gap-2 px-3 py-2 text-sm rounded-xl transition-all
                  ${syncing
                    ? "bg-[var(--calm-bg)] text-[var(--calm)]"
                    : "text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--cream)]"
                  }
                  disabled:cursor-not-allowed
                `}
                title={lastSyncAt ? `Last sync: ${formatLastSync(lastSyncAt)}` : "Sync with LMS"}
              >
                <svg
                  className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline font-medium">
                  {syncing ? "Syncing..." : formatLastSync(lastSyncAt)}
                </span>
              </button>
            )}

            {/* Settings */}
            <Link
              to="/settings"
              className={`p-2.5 rounded-xl transition-all ${
                location.pathname === "/settings"
                  ? "bg-[var(--gold)]/10 text-[var(--gold)]"
                  : "text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--cream)]"
              }`}
              title="Settings"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </Link>

            {/* Sign Out */}
            <button
              onClick={signOut}
              className="p-2.5 text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--cream)] rounded-xl transition-all"
              title="Sign out"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
